import logo from '@assets/images/static/img_logo_byes.png';
import menuQuit from '@assets/images/static/menu-pause-quit.png';
import menuImg from '@assets/images/static/menu-pause.png';
import { useLingui } from '@lingui/react';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { styled } from 'styled-components';
import useSoundAudio from '~/helpers/hooks/soundAudio';
import { Contact } from '~/pages/components/game/contact';
import { Mentions } from '~/pages/components/game/mentions';
import { Rules } from '~/pages/components/game/rules';
import Button from '~/pages/components/ui/button';
import { classic } from '~/themes';

type IProps = {
  isOpen: boolean;
  close: () => void;
  onClick?: () => void;
  $menuWithLogo?: boolean;
};

type TProps = IProps;

export function Menu({ onClick, isOpen, $menuWithLogo, close }: TProps) {
  const navigate = useNavigate();
  const { playSound } = useSoundAudio();
  const [itemSelected, setItemSelected] = useState<
    'rules' | 'contact' | 'mentions' | 'hub' | null
  >(null);
  const { i18n } = useLingui();
  const { site, thematique } = useParams();
  const [menuTitle, setMenuTitle] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (site === 'ideev' && thematique) {
      setMenuTitle(i18n._('site.ideev.title'));
    }

    if (site === 'hm' && thematique) {
      setMenuTitle(i18n._('site.hm.title'));
    }
  }, []);

  function menuBtnClick() {
    if (itemSelected) setItemSelected(null);
  }

  return (
    <Container>
      <SDivMenuBtn
        onClick={() => {
          playSound('click');
          onClick && onClick();
        }}
      >
        <SButton onClick={menuBtnClick}>
          <img src={isOpen ? menuQuit : menuImg} alt="" />
        </SButton>
      </SDivMenuBtn>
      <SDivBackground
        hidden={!isOpen}
        height={window.innerHeight}
        onClick={() => {
          if (itemSelected) setItemSelected(null);
          onClick && onClick();
        }}
      ></SDivBackground>
      <SDivMenuContent
        className={isOpen && !itemSelected ? 'open' : ''}
        height={window.innerHeight}
      >
        <SDivTransitionWrapper className={!itemSelected ? '' : 'selected'}>
          <Button
            margin="15px"
            color="white"
            hidden={!isOpen || !(!itemSelected || itemSelected === 'rules')}
            minHeight="46px"
            textColor={`${classic.token?.colorTextSecondary}`}
            onClick={() => {
              playSound('click');
              setItemSelected('rules');
            }}
            width="72%"
          >
            {i18n._('menu.rules')}
          </Button>
          <Button
            margin="15px"
            color="white"
            hidden={!isOpen || !(!itemSelected || itemSelected === 'contact')}
            minHeight="46px"
            textColor={`${classic.token?.colorTextSecondary}`}
            onClick={() => {
              playSound('click');
              setItemSelected('contact');
            }}
            width="72%"
          >
            {i18n._('menu.contact')}
          </Button>
          <Button
            margin="15px"
            color="white"
            hidden={!isOpen || !(!itemSelected || itemSelected === 'mentions')}
            minHeight="46px"
            textColor={`${classic.token?.colorTextSecondary}`}
            onClick={() => {
              playSound('click');
              setItemSelected('mentions');
            }}
            width="72%"
          >
            {i18n._('menu.mentions')}
          </Button>
          <Button
            margin="15px"
            color="white"
            hidden={!isOpen || !(!itemSelected || itemSelected === 'hub')}
            minHeight="46px"
            textColor={`${classic.token?.colorTextSecondary}`}
            onClick={() => {
              setItemSelected(null);
              playSound('click');
              close();
              navigate(`/`);
            }}
            width="72%"
          >
            {i18n._('menu.hub')}
          </Button>
        </SDivTransitionWrapper>
      </SDivMenuContent>
      <SDivItemContent
        height={window.innerHeight}
        className={itemSelected ? 'open' : ''}
      >
        <SDivGroupe hidden={!itemSelected} height="85%">
          <SDivGroupeContent>
            <Mentions hidden={!itemSelected || itemSelected !== 'mentions'} />
            <Rules hidden={!itemSelected || itemSelected !== 'rules'} />
            <Contact hidden={!itemSelected || itemSelected !== 'contact'} />
          </SDivGroupeContent>

          <SDivGroupeBtn>
            <Button
              margin="15px"
              color="secondary"
              hidden={!itemSelected}
              minHeight="46px"
              textColor={`${classic.token?.colorWhite}`}
              onClick={() => {
                playSound('click');
                setItemSelected(null);
              }}
              width="72%"
            >
              {i18n._('common.back')}
            </Button>
          </SDivGroupeBtn>
        </SDivGroupe>
      </SDivItemContent>
      {$menuWithLogo && (
        <>
          <SLogo
            className={isOpen ? 'open' : menuTitle ? 'notvisible' : ''}
            src={logo}
            alt=""
          />
        </>
      )}
      <SPMenuTitle className={menuTitle && !isOpen ? 'visible' : ''}>
        {menuTitle}
      </SPMenuTitle>
    </Container>
  );
}

const Container = styled.div`
  align-items: center;
  background-color: #33b2cf;
  border-bottom: solid 2px #fff;
  display: flex;
  justify-content: flex-start;
  flex: 0 0 42px;
  position: relative;
  user-select: none;
  width: 100%;
`;

const SPMenuTitle = styled.p`
  align-items: center;
  display: flex;
  color: ${classic.token?.colorWhite};
  justify-content: center;
  left: 0;
  top: 0;
  font-size: 1.25rem;
  font-weight: 700;
  height: 100%;
  width: 100%;
  opacity: 0;
  position: absolute;
  transform: translateX(0);
  transition: opacity 0.6s linear;
  pointer-events: none;
  text-transform: uppercase;

  &.visible {
    opacity: 1;
    transition: opacity 0.6s linear;
  }
`;

const SDivGroupe = styled.div<{
  hidden?: boolean;
  height?: string;
  justifyContent?: string;
  alignItems?: string;
}>`
  align-items: ${({ alignItems }) => (alignItems ? alignItems : 'center')};
  display: flex;
  justify-content: ${({ justifyContent }) =>
    justifyContent ? justifyContent : 'space-between'};
  flex-direction: column;
  height: ${({ height }) => (height ? height : 'auto')};
  transition: all 0.6s linear;
  width: 100%;
`;

const SDivGroupeBtn = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  flex: 0 0 15%;
  width: 100%;
`;

const SDivGroupeContent = styled.div`
  flex: 0 0 85%;
  overflow: hidden;
  width: 100%;
`;

const SLogo = styled.img`
  bottom: -22px;
  position: absolute;
  object-fit: contain;
  height: 48px;
  transition: all 1s linear;
  opacity: 1;
  width: 100%;
  z-index: 5;
  pointer-events: none;
  &.open {
    bottom: 3px;
    opacity: 1;

    height: 90%;
    transition: all 1s ease-out;
  }

  &.notvisible {
    transition: all 1s linear;
    opacity: 0;
  }
`;

const SDivMenuBtn = styled.div`
  clip-path: polygon(0 0, 100% 0%, 80% 100%, 0% 100%);
  height: 100%;
  padding: 0 15px 0 0;
  width: 20%;
`;

const SDivBackground = styled.div<{ hidden?: boolean; height: number }>`
  height: ${({ height }) => `${height}px`};
  max-height: ${({ height }) => `${height}px`};
  position: absolute;
  top: 42px;
  z-index: 400;
  width: 100%;
`;

const SDivItemContent = styled.div<{ hidden?: boolean; height: number }>`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: absolute;
  top: 42px;
  left: 0;
  height: ${({ height }) => `${height}px`};
  max-height: ${({ height }) => `${height}px`};
  transition: all 1s linear;
  transform: translateX(-150%);
  opacity: 0;
  width: 90%;
  z-index: 500;

  &.open {
    opacity: 1;
    background-color: #009fc3;
    transition: all 1s ease-out;
    height: ${({ height }) => `${height}px`};
    max-height: ${({ height }) => `${height}px`};
    transform: translateX(0);
    width: 90%;
    @media screen and (max-width: 415px) {
      width: 100%;
    }
    @media screen and (max-height: 600px) {
      width: 100%;
    }
  }
`;

const SDivMenuContent = styled.div<{ hidden?: boolean; height: number }>`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: absolute;
  top: 42px;
  left: 0;
  height: ${({ height }) => `${height}px`};
  max-height: ${({ height }) => `${height}px`};
  transform: translateX(-150%);
  transition: all 1s linear;
  opacity: 0;
  width: 60%;
  z-index: 500;

  &.open {
    opacity: 1;
    background-color: #009fc3;
    transition: all 1s ease-out;
    transform: translateX(0);
    height: ${({ height }) => `${height}px`};
    max-height: ${({ height }) => `${height}px`};
    width: 60%;
    @media screen and (max-width: 415px) {
      width: 80%;
    }
    @media screen and (max-height: 600px) {
      width: 80%;
    }
  }
`;

const SDivTransitionWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  height: auto;
  width: 100%;
  &.selected {
    height: 100%;
    padding-top: 30px;
    transition: height 1s ease-out;
  }
`;

const SButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
  padding: 5px 10px;
  font-size: 1rem;
  height: 100%;
  position: relative;
  width: 100%;
  z-index: 10;
  &:focus {
    font-weight: bold;
    outline: 0;
  }
  img {
    left: 0;
    top: 0;
    object-fit: contain;
    height: 100%;

    position: absolute;
  }
`;

/*
animation ancienne

 <SDivMenuContent
        className={!isOpen ? '' : itemSelected ? 'selected' : 'open'}
        height={window.innerHeight}
      >
        <SDivTransitionWrapper className={!itemSelected ? '' : 'selected'}>
          <Button
            margin="15px"
            color="white"
            hidden={!isOpen || !(!itemSelected || itemSelected === 'rules')}
            minHeight="46px"
            textColor={`${classic.token?.colorTextSecondary}`}
            onClick={() => setItemSelected('rules')}
            width="72%"
          >
            {i18n._('menu.rules')}
          </Button>
          <Button
            margin="15px"
            color="white"
            hidden={!isOpen || !(!itemSelected || itemSelected === 'contact')}
            minHeight="46px"
            textColor={`${classic.token?.colorTextSecondary}`}
            onClick={() => setItemSelected('contact')}
            width="72%"
          >
            {i18n._('menu.contact')}
          </Button>
          <Button
            margin="15px"
            color="white"
            hidden={!isOpen || !(!itemSelected || itemSelected === 'mentions')}
            minHeight="46px"
            textColor={`${classic.token?.colorTextSecondary}`}
            onClick={() => setItemSelected('mentions')}
            width="72%"
          >
            {i18n._('menu.mentions')}
          </Button>
          <Button
            margin="15px"
            color="white"
            hidden={!isOpen || !(!itemSelected || itemSelected === 'hub')}
            minHeight="46px"
            textColor={`${classic.token?.colorTextSecondary}`}
            onClick={() => {
              close();
              navigate(`/`);
            }}
            width="72%"
          >
            {i18n._('menu.hub')}
          </Button>

          <SDivGroupe hidden={!itemSelected} height="80%">
            <SDivGroupeContent>
              <Mentions hidden={!itemSelected || itemSelected !== 'mentions'} />
              <Rules hidden={!itemSelected || itemSelected !== 'rules'} />
              <Contact hidden={!itemSelected || itemSelected !== 'contact'} />
            </SDivGroupeContent>

            <SDivGroupeBtn>
              <Button
                margin="15px"
                color="secondary"
                hidden={!itemSelected}
                minHeight="46px"
                textColor={`${classic.token?.colorWhite}`}
                onClick={() => setItemSelected(null)}
                width="72%"
              >
                {i18n._('common.back')}
              </Button>
            </SDivGroupeBtn>
          </SDivGroupe>
        </SDivTransitionWrapper>
      </SDivMenuContent>
*/
